import i18next from 'i18next';
import { CboRole, FeatureFlag, Package } from '@cbo/shared-library';
import isPermitted from '../../../lib/permissions';
import { useUsers } from '../../../contexts/userContext';
import routes from '../../../constants/routes';
import { NavItem } from '.';
import { useFeatureFlags } from '../../../utils/hooks/useFeatureFlag';
import useCountingAndLocationRoutes from './useCountingAndLocationRoutes';
import { usePackage } from '../../../contexts/packageContext';

export default function useInventoryRoutes(): NavItem[] {
  const user = useUsers();
  const [
    mobileInventoryEnabled,
    purchaseOrdering,
    inventoryCoreEnabled,
    cogs,
    perpetualInventoryReport,
    lowStockReport,
    top20ItemsReport,
    realTimeInventory,
  ] = useFeatureFlags(
    FeatureFlag.mobileInventory,
    FeatureFlag.purchaseOrdering,
    FeatureFlag.corePackageInventory,
    FeatureFlag.cogs,
    FeatureFlag.perpetualInventoryReport,
    FeatureFlag.lowStockReport,
    FeatureFlag.top20ItemsReport,
    FeatureFlag.realTimeInventory
  );
  const inventoryPackageCore = usePackage(Package.INVENTORY);
  const inventoryRoutes: NavItem[] = [];
  const inventoryReportRoutes: NavItem[] = [];
  const countingAndLocationsRoutes = useCountingAndLocationRoutes();

  if (isPermitted(user, [CboRole.VENDOR_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.vendors'),
      href: routes.VENDORS,
      testid: 'vendors',
    });
  }

  if (
    inventoryPackageCore &&
    inventoryCoreEnabled &&
    purchaseOrdering &&
    isPermitted(user, [CboRole.PURCHASE_ORDER_VIEW])
  ) {
    inventoryRoutes.push({
      title: i18next.t('navItems.purchaseOrders'),
      href: routes.PURCHASE_ORDERS,
      testid: 'purchase-orders',
    });
  }

  if (isPermitted(user, [CboRole.RAW_ITEM_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.rawItems'),
      href: routes.RAW_ITEMS,
      testid: 'raw-items',
    });
  }

  if (inventoryPackageCore && inventoryCoreEnabled && isPermitted(user, [CboRole.UNITS_OF_MEASURE_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.unitsOfMeasure'),
      href: routes.UNITS_OF_MEASURE,
      testid: 'units-of-measure',
    });
  }

  if (isPermitted(user, [CboRole.INVOICE_VIEW])) {
    inventoryRoutes.push({
      title: i18next.t('navItems.invoices'),
      href: routes.INVOICES,
      testid: 'invoice-items',
    });
  }

  if (isPermitted(user, [CboRole.INVOICE_VIEW])) {
    inventoryReportRoutes.push({
      title: i18next.t('navItems.invoiceHistory'),
      href: routes.INVOICE_HISTORY_REPORT,
      testid: 'invoice-history-report',
    });
  }

  if (inventoryCoreEnabled && inventoryPackageCore) {
    const menu = [];

    if (mobileInventoryEnabled && isPermitted(user, [CboRole.MOBILE_INVENTORY_VIEW])) {
      inventoryRoutes.push({
        title: i18next.t('navItems.countingAndLocations'),
        href: routes.COUNTING_AND_LOCATIONS,
        testid: 'counting-and-locations',
        forceNewFrame: true,
        backHref: '/inventory',
        children: countingAndLocationsRoutes,
      });
    }

    if (isPermitted(user, [CboRole.SALES_ITEM_VIEW])) {
      menu.push({
        title: i18next.t('navItems.salesItems'),
        href: routes.SALES_ITEMS,
        testid: 'sales-items',
      });
      menu.push({
        title: i18next.t('navItems.modifierGroups'),
        href: routes.MODIFIER_GROUPS,
        testid: 'modifier-groups',
      });
    }

    if (isPermitted(user, [CboRole.RECIPE_VIEWER])) {
      menu.push({
        title: i18next.t('navItems.recipes'),
        href: routes.RECIPES,
        testid: 'recipes',
      });
    }

    if (menu.length > 0) {
      inventoryRoutes.push({
        title: i18next.t('navItems.menu'),
        href: routes.MENU,
        testid: 'menu',
        forceNewFrame: true,
        backHref: '/inventory',
        children: menu,
      });
    }

    if (cogs && isPermitted(user, [CboRole.COGS_REPORT_VIEW])) {
      inventoryReportRoutes.push({
        title: i18next.t('navItems.cogs'),
        href: routes.COGS_REPORT,
        testid: 'cogs-report',
      });
    }

    if (lowStockReport && isPermitted(user, [CboRole.LOW_STOCK_REPORT_VIEW])) {
      inventoryReportRoutes.push({
        title: i18next.t('navItems.lowStockReport'),
        href: routes.LOW_STOCK_REPORT,
        testid: 'low-stock-report',
      });
    }
    if (perpetualInventoryReport && isPermitted(user, [CboRole.PERPETUAL_INVENTORY_REPORT_VIEW])) {
      inventoryReportRoutes.push({
        title: i18next.t('navItems.perpetualInventoryOnHand'),
        href: routes.PERPETUAL_INVENTORY_ON_HAND,
        testid: 'perpetual-inventory-on-hand-report',
      });
    }

    if (top20ItemsReport && isPermitted(user, [CboRole.CBO_REPORT_TOP20_BOTTOM10_VIEWER])) {
      inventoryReportRoutes.push({
        title: i18next.t('navItems.top20ItemUsageReport'),
        href: routes.TOP_20_ITEM_USAGE_REPORT,
        testid: 'top-20-item-usage-report',
      });
    }

    if (realTimeInventory) {
      inventoryReportRoutes.push({
        title: i18next.t('navItems.realTimeInventory'),
        href: routes.REAL_TIME_INVENTORY,
        testid: 'real-time-inventory',
      });
    }
  }

  if (inventoryReportRoutes.length > 0) {
    inventoryRoutes.push({
      title: i18next.t('navItems.reports'),
      testid: 'inventory-reports',
      children: inventoryReportRoutes,
    });
  }

  return inventoryRoutes;
}
